/**
 * Probookingsettings.jsx — Stylist & service matrix for online booking.
 *
 * Booking rules (deposit, hours, slug etc.) have moved to ProBookingConfig.jsx.
 * This page is now purely about which stylists and services are online-bookable.
 *
 * Route: /pro/booking-settings  (ACCOUNT_OWNER)
 *
 * GET /api/pro-booking/stylist-services → { stylists, services, assignments }
 * GET /api/pro-booking/stylist-settings → { stylists: [...] }
 * PUT /api/pro-booking/stylist-settings/:id
 * POST /api/pro-booking/stylist-services
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableHead, TableBody,
  TableRow, TableCell, Checkbox, Switch, Button, Chip,
  CircularProgress, Alert, Tooltip, IconButton,
} from '@mui/material';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import CancelIcon       from '@mui/icons-material/Cancel';
import SaveIcon         from '@mui/icons-material/Save';
import RefreshIcon      from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon    from '@mui/icons-material/ArrowBack';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('access_token');
  const res = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
};


// ─────────────────────────────────────────────────────────────────────────────
// Stylist-Service Assignment Matrix
// ─────────────────────────────────────────────────────────────────────────────
function StylistServiceMatrix() {
  const [matrix,  setMatrix]  = useState(null);
  const [dirty,   setDirty]   = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [saved,   setSaved]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/api/pro-booking/stylist-services');
      setMatrix(data);
      setDirty({});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isActive = (empId, svcId) => {
    const key = `${empId}:${svcId}`;
    if (key in dirty) return dirty[key];
    return Boolean(matrix?.assignments?.[key]);
  };

  const toggle = (empId, svcId) => {
    const key = `${empId}:${svcId}`;
    setDirty(prev => ({ ...prev, [key]: !isActive(empId, svcId) }));
    setSaved(false);
  };

  const toggleRow = (empId, currentlyAllOn) => {
    const updates = {};
    matrix.services.forEach(s => { updates[`${empId}:${s.id}`] = !currentlyAllOn; });
    setDirty(prev => ({ ...prev, ...updates }));
    setSaved(false);
  };

  const toggleCol = (svcId, currentlyAllOn) => {
    const updates = {};
    matrix.stylists.forEach(e => { updates[`${e.id}:${svcId}`] = !currentlyAllOn; });
    setDirty(prev => ({ ...prev, ...updates }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const assignments = [];
    matrix.stylists.forEach(emp => {
      matrix.services.forEach(svc => {
        if (isActive(emp.id, svc.id)) {
          assignments.push({ employee_id: emp.id, service_id: svc.id });
        }
      });
    });
    try {
      await apiFetch('/api/pro-booking/stylist-services', {
        method: 'POST',
        body: JSON.stringify({ assignments }),
      });
      setSaved(true);
      setDirty({});
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!matrix) return null;

  const { stylists, services } = matrix;
  const hasDirty = Object.keys(dirty).length > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Service Assignments</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Reload">
            <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={save}
            disabled={!hasDirty || saving}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {saved    && <Alert severity="success" sx={{ mb: 2 }}>Assignments saved.</Alert>}
      {hasDirty && !saved && <Alert severity="info" sx={{ mb: 2 }}>You have unsaved changes.</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tick a cell to allow that stylist to take online bookings for that service.
        Only ticked combinations appear on the public booking page.
      </Typography>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 400 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, minWidth: 160, borderBottom: 2 }}>
                Stylist ↓ / Service →
              </TableCell>
              {services.map(svc => {
                const allOn = stylists.every(e => isActive(e.id, svc.id));
                return (
                  <TableCell key={svc.id} align="center" sx={{ fontWeight: 600, borderBottom: 2, minWidth: 110 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <span>{svc.name}</span>
                      <Tooltip title={allOn ? 'Deselect all' : 'Select all'}>
                        <Chip
                          label={allOn ? 'All ✓' : 'All'}
                          size="small"
                          variant="outlined"
                          onClick={() => toggleCol(svc.id, allOn)}
                          sx={{ cursor: 'pointer', fontSize: 10 }}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {stylists.map(emp => {
              const allOn = services.every(s => isActive(emp.id, s.id));
              return (
                <TableRow key={emp.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {emp.first_name} {emp.last_name}
                      <Tooltip title={allOn ? 'Remove all' : 'Assign all'}>
                        <Chip
                          label={allOn ? 'All ✓' : 'All'}
                          size="small"
                          variant="outlined"
                          onClick={() => toggleRow(emp.id, allOn)}
                          sx={{ cursor: 'pointer', fontSize: 10 }}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  {services.map(svc => (
                    <TableCell key={svc.id} align="center" padding="checkbox">
                      <Checkbox
                        checked={isActive(emp.id, svc.id)}
                        onChange={() => toggle(emp.id, svc.id)}
                        size="small"
                        color="primary"
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Per-Stylist Booking Settings & Gate Status
// ─────────────────────────────────────────────────────────────────────────────
function StylistBookingSettings() {
  const [stylists, setStylists] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [toggling, setToggling] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/api/pro-booking/stylist-settings');
      setStylists(data.stylists || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleBookingEnabled = async (empId, currentVal) => {
    setToggling(prev => ({ ...prev, [empId]: true }));
    try {
      await apiFetch(`/api/pro-booking/stylist-settings/${empId}`, {
        method: 'PUT',
        body: JSON.stringify({ online_booking_enabled: currentVal ? 0 : 1 }),
      });
      setStylists(prev =>
        prev.map(s =>
          s.employee_id === empId
            ? { ...s, online_booking_enabled: currentVal ? 0 : 1 }
            : s
        )
      );
    } catch (e) {
      if (e.message.includes('no name set')) {
        setError(`Cannot enable online booking: ${e.message} Go to Team → Employee Card to add their name.`);
      } else {
        setError(e.message);
      }
    } finally {
      setToggling(prev => ({ ...prev, [empId]: false }));
    }
  };

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;

  const GateIcon = ({ ok, label }) => (
    <Tooltip title={label}>
      {ok
        ? <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
        : <CancelIcon color="error" sx={{ fontSize: 18 }} />
      }
    </Tooltip>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">Stylist Online Booking</Typography>
        <Tooltip title="Both gates must be green for a stylist to appear on the public booking page">
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
        <IconButton onClick={load} size="small" sx={{ ml: 'auto' }}><RefreshIcon /></IconButton>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Gate 1: Rota configured (published shift or recurring schedule).
        Gate 2: Online booking enabled here. Both must be green before a
        stylist appears on the public booking page.
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Stylist</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700 }}>Gate 1: Rota</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700 }}>Gate 2: Online Booking</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stylists.map(s => {
            const rota_ok    = Boolean(s.has_rota);
            const booking_ok = Boolean(s.online_booking_enabled) && Boolean(s.online_bookable);
            const live       = rota_ok && booking_ok;

            return (
              <TableRow key={s.employee_id} hover>
                <TableCell sx={{ fontWeight: 500 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {(s.first_name || s.last_name)
                      ? `${s.first_name || ''} ${s.last_name || ''}`.trim()
                      : <em style={{ color: '#999' }}>No name set</em>
                    }
                    {!s.first_name && !s.last_name && (
                      <Tooltip title="Employee has no name — update their record before enabling online booking">
                        <Chip label="Name missing" size="small" color="warning" variant="outlined" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <GateIcon
                    ok={rota_ok}
                    label={rota_ok
                      ? 'Rota configured'
                      : 'No rota — add shifts or a recurring schedule first'}
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    {toggling[s.employee_id]
                      ? <CircularProgress size={20} />
                      : (
                        <Switch
                          checked={Boolean(s.online_booking_enabled)}
                          onChange={() => toggleBookingEnabled(s.employee_id, s.online_booking_enabled)}
                          size="small"
                        />
                      )
                    }
                    {!s.online_bookable && (
                      <Tooltip title="Employee online_bookable flag is off — edit the employee card to enable">
                        <Chip label="Flag off" size="small" color="warning" variant="outlined" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={live ? 'Live' : 'Not live'}
                    size="small"
                    color={live ? 'success' : 'default'}
                    variant={live ? 'filled' : 'outlined'}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Root export
// ─────────────────────────────────────────────────────────────────────────────
export default function ProBookingSettings() {
  const navigate = useNavigate();
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
    <IconButton size="small" onClick={() => navigate('/pro/booking-settings')}>
      <ArrowBackIcon />
    </IconButton>
    <Typography variant="h5" sx={{ fontWeight: 700 }}>
      Online Booking — Stylist Settings
    </Typography>
  </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure which stylists and services are available on your public
        booking page. Page settings, salon hours, and deposit rules are in{' '}
        <strong>Booking Configuration</strong>.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <StylistBookingSettings />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <StylistServiceMatrix />
      </Paper>
    </Box>
  );
}